import { EnhancedDataTable } from "adizen-tanstack-table";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { authStore } from "@/stores/authStore";
import { useSnapshot } from "valtio";
import { getTestStats } from "@/api/testAnalysis";
import CertificateModal from "@/components/certificates/CertificateModal";

/**
 * Certificate status for an assessment row. Eligibility belongs to the course
 * (the server's `assessmentPassed`: any passing attempt), so an abandoned or
 * failed retake no longer shows "Not Eligible" next to a passed course.
 */
function certificationStatus(stat) {
  const passed =
    typeof stat.assessmentPassed === "boolean"
      ? stat.assessmentPassed
      : stat.percentage >= stat.test?.passingScore;
  if (passed) return "eligible";
  if (stat.status === "IN_PROGRESS") return "in-progress";
  return "not-eligible";
}

/**
 * How the "Passed" cell should read for one attempt row. Every attempt is
 * listed, including ones the learner opened and never submitted (status stays
 * IN_PROGRESS at 0%) and failed retakes of an assessment they already passed.
 * Those used to show a red "NO" / 0% beside a passed course, which read as
 * "you failed" and produced a steady stream of support tickets.
 */
function attemptOutcome(stat) {
  if (stat.status !== "COMPLETED") return "unfinished";
  if (stat.passed) return "passed";
  if (stat.testType === "ASSESSMENT" && stat.assessmentPassed === true) {
    return "failed-retake";
  }
  return "failed";
}

const OUTCOME_BADGE = {
  passed: { label: "YES", className: "bg-green-100 text-green-800" },
  failed: { label: "NO", className: "bg-red-100 text-red-800" },
  "failed-retake": {
    label: "NO (passed in another attempt)",
    className: "bg-gray-100 text-gray-700",
  },
  unfinished: {
    label: "NOT SUBMITTED",
    className: "bg-gray-100 text-gray-700",
  },
};

const OUTCOME_EXPORT = {
  passed: "Yes",
  failed: "No",
  "failed-retake": "No (passed in another attempt)",
  unfinished: "Not submitted",
};

export default function TestAnalytics() {
  const snap = useSnapshot(authStore);
  const navigate = useNavigate();
  const cdcNumber = snap.user.indianCdc;
  const [certificateCourseId, setCertificateCourseId] = useState(null);

  if (!cdcNumber) {
    toast.error("Please complete your profile to view this page");
    navigate("/seafarer/profile");
    return;
  }

  // Must be referentially stable: the table's internal fetch effect keys off
  // this callback's identity, so a new function per render causes duplicate
  // requests.
  const fetchTestStats = useCallback(async (params) => {
    try {
      const {
        page = 1,
        limit = 10,
        search,
        sortBy,
        sortOrder,
        status,
        testType,
        difficulty,
        passed,
        dateFrom,
        dateTo,
        percentage,
        attemptNumber,
        testTitle,
        courseName,
        materialTitle,
        userEmail,
        userMobileNumber,
        indianCdc,
        needsManualGrade,
        ...otherParams
      } = params || {};

      // Build filters object for API
      const filters = {
        page,
        limit,
        sortBy: sortBy || "createdAt",
        sortOrder: sortOrder || "desc",
      };

      if (cdcNumber) {
        filters.cdc = cdcNumber;
      }
      // Add search if provided
      if (search) {
        filters.search = search;
      }

      // Handle status filter
      if (status && status !== "all") {
        filters.status = status;
      }

      // Handle test type filter
      if (testType && testType !== "all") {
        filters.testType = testType;
      }

      if (percentage) {
        filters.percentageMin = percentage;
        filters.percentageMax = percentage;
      }

      // Handle difficulty filter
      if (difficulty && difficulty !== "all") {
        filters.difficulty = difficulty;
      }

      // Handle passed filter
      if (passed && passed !== "all") {
        filters.passed = passed === "true";
      }

      // Handle needsManualGrade filter
      if (needsManualGrade && needsManualGrade !== "all") {
        filters.needsManualGrade = needsManualGrade === "true";
      }

      // Handle date range filters
      if (dateFrom) {
        filters.dateFrom = dateFrom;
      }
      if (dateTo) {
        filters.dateTo = dateTo;
      }

      if (attemptNumber) {
        filters.attemptNumber = parseInt(attemptNumber);
      }

      // Handle specific filters
      if (testTitle) filters.testTitle = testTitle;
      if (courseName) filters.courseName = courseName;
      if (materialTitle) filters.materialTitle = materialTitle;
      if (userEmail) filters.userEmail = userEmail;
      if (indianCdc) filters.indianCdc = indianCdc;

      // Add any other parameters that weren't explicitly handled
      Object.keys(otherParams).forEach((key) => {
        if (otherParams[key] && otherParams[key] !== "all") {
          filters[key] = otherParams[key];
        }
      });

      const response = await getTestStats(filters);
      if (response.success && Array.isArray(response.data.stats)) {
        const mappedStats = response.data.stats.map((stat) => ({
          id: stat.id,
          userId: stat.userId,
          testId: stat.testId,
          attemptNumber: stat.attemptNumber,
          status: stat.status,
          startedAt: stat.startedAt,
          completedAt: stat.completedAt,
          timeSpent: stat.timeSpent,
          timeRemaining: stat.timeRemaining,
          totalQuestions: stat.totalQuestions,
          answeredQuestions: stat.answeredQuestions,
          correctAnswers: stat.correctAnswers,
          incorrectAnswers: stat.incorrectAnswers,
          skippedQuestions: stat.skippedQuestions,
          rawScore: stat.rawScore,
          maxScore: stat.maxScore,
          percentage: stat.percentage,
          passed: stat.passed,
          // Course-level: true if ANY completed attempt on this assessment
          // passed (the certificate rule), not just this row.
          assessmentPassed: stat.assessmentPassed,
          needsManualGrade: stat.needsManualGrade,
          createdAt: stat.createdAt,
          updatedAt: stat.updatedAt,
          date: stat.date,
          testsAttempted: stat.testsAttempted,
          testsAnsweredCorrectly: stat.testsAnsweredCorrectly,
          testsAnsweredIncorrectly: stat.testsAnsweredIncorrectly,

          // User information - ONLY name, email, mobile, CDC
          userName: stat.user?.name,
          userEmail: stat.user?.email,
          userCdc: stat.user?.indianCdc,

          // Test information
          test: stat.test,
          courseId: stat.test?.course?.id,
          testTitle: stat.test?.title,
          testType: stat.test?.type,
          testDifficulty: stat.test?.difficulty,
          testTimeLimit: stat.test?.timeLimit,
          testMaxAttempts: stat.test?.maxAttempts,
          testPassingScore: stat.test?.passingScore,
          testIsActive: stat.test?.isActive,

          // Material information (if available)
          materialTitle: stat.test?.learningMaterial?.title,
          materialTopicName: stat.test?.learningMaterial?.topic?.name,
          materialCourseName: stat.test?.learningMaterial?.topic?.course?.name,

          // Course information (if test is course-level)
          courseName: stat.test?.course?.name,

          // Raw data for actions
          _raw: stat,
        }));

        return {
          success: true,
          message: response.message,
          data: mappedStats,
          meta: {
            total: response.data.pagination?.total || mappedStats.length,
            page: response.data.pagination?.page || page,
            limit: response.data.pagination?.limit || limit,
            totalPages:
              response.data.pagination?.totalPages ||
              Math.ceil(mappedStats.length / limit),
            hasNext: response.data.pagination?.hasNext || false,
            hasPrev: response.data.pagination?.hasPrev || false,
          },
        };
      } else {
        throw new Error(response.message || "Failed to fetch test statistics");
      }
    } catch (error) {
      console.error("Error fetching test stats:", error);
      toast.error("Failed to fetch test statistics");
      return {
        success: false,
        message: error.message,
        data: [],
        meta: {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  }, [cdcNumber]);

  // Built here rather than via useServerSideTableData: that hook returns a new
  // config object (and a new onDataRequest closure) on every render, which
  // re-triggers the table's internal fetch effect and produces duplicate
  // requests. A memoised config keeps the identity stable.
  const tableServerSide = useMemo(
    () => ({ enabled: true, onDataRequest: fetchTestStats, debounceMs: 500 }),
    [fetchTestStats]
  );

  const handleViewCertificate = (courseId) => {
    if (!courseId) {
      toast.error("Course not found for this certificate.");
      return;
    }
    setCertificateCourseId(courseId);
  };
  const columns = useMemo(() => {
    return [
      {
        id: "serialNumber",
        header: "S.No",
        accessorFn: (row, index) => index + 1,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 60,
      },
      {
        id: "testTitle",
        header: "Test Title",
        accessorKey: "testTitle",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testTitle}</span>
        ),
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "testTitle",
        size: 200,
        exportConfig: {
          exportHeader: "Test Title",
          exportValueFn: (stat) => stat.testTitle,
        },
      },
      {
        id: "testType",
        header: "Test Type",
        accessorKey: "testType",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testType}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Quiz", value: "QUIZ" },
          { label: "Assessment", value: "ASSESSMENT" },
        ],
        serverFilterKey: "testType",
        serverSortKey: "testType",
        size: 100,
        cell: ({ getValue }) => {
          const type = getValue();
          return (
            <Badge
              className={
                type === "QUIZ"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-purple-100 text-purple-800"
              }
            >
              {type}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Test Type",
          exportValueFn: (stat) => stat.testType,
        },
      },
      {
        id: "testDifficulty",
        header: "Difficulty",
        accessorKey: "testDifficulty",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testDifficulty}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Beginner", value: "BEGINNER" },
          { label: "Intermediate", value: "INTERMEDIATE" },
          { label: "Advanced", value: "ADVANCED" },
        ],
        serverFilterKey: "difficulty",
        serverSortKey: "testDifficulty",
        size: 120,
        cell: ({ getValue }) => {
          const difficulty = getValue();
          let color = "bg-gray-100 text-gray-800";
          if (difficulty === "BEGINNER") color = "bg-green-100 text-green-800";
          else if (difficulty === "INTERMEDIATE")
            color = "bg-yellow-100 text-yellow-800";
          else if (difficulty === "ADVANCED") color = "bg-red-100 text-red-800";

          return <Badge className={color}>{difficulty}</Badge>;
        },
        exportConfig: {
          exportHeader: "Difficulty",
          exportValueFn: (stat) => stat.testDifficulty,
        },
      },
      {
        id: "passed",
        header: "Passed",
        accessorKey: "passed",
        // Must return the boolean: it used to return a <span> element, which
        // is always truthy, so every attempt (even 0%) showed "YES".
        accessorFn: (stat) => stat.passed,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        serverFilterKey: "passed",
        serverSortKey: "passed",
        size: 100,
        cell: ({ row }) => {
          const { label, className } = OUTCOME_BADGE[attemptOutcome(row.original)];
          return <Badge className={`${className} text-nowrap`}>{label}</Badge>;
        },
        exportConfig: {
          exportHeader: "Passed",
          exportValueFn: (stat) => OUTCOME_EXPORT[attemptOutcome(stat)],
        },
      },
      {
        id: "testPassingScore",
        header: "Passing Score",
        accessorKey: "testPassingScore",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testPassingScore}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "percentage",
        serverSortKey: "testPassingScore",
        size: 120,
        cell: ({ getValue }) => {
          const score = getValue();
          return <span>{score}%</span>;
        },
        exportConfig: {
          exportHeader: "Passing Score",
          exportValueFn: (stat) => stat.testPassingScore,
        },
      },
      {
        id: "materialTitle",
        header: "Material Title",
        accessorKey: "materialTitle",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.materialTitle}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialTitle",
        size: 200,
        exportConfig: {
          exportHeader: "Material Title",
          exportValueFn: (stat) => stat.materialTitle,
        },
      },
      {
        id: "materialTopicName",
        header: "Topic Name",
        accessorKey: "materialTopicName",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.materialTopicName}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialTopicName",
        size: 150,
        exportConfig: {
          exportHeader: "Topic Name",
          exportValueFn: (stat) => stat.materialTopicName,
        },
      },
      {
        id: "materialCourseName",
        header: "Material Course",
        accessorKey: "materialCourseName",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.materialCourseName}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialCourseName",
        size: 150,
        exportConfig: {
          exportHeader: "Material Course",
          exportValueFn: (stat) => stat.materialCourseName,
        },
      },
      {
        id: "courseName",
        header: "Course Name",
        accessorKey: "courseName",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.courseName}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "courseName",
        size: 150,
        exportConfig: {
          exportHeader: "Course Name",
          exportValueFn: (stat) => stat.courseName,
        },
      },
      {
        id: "attemptNumber",
        header: "Attempt Number",
        accessorKey: "attemptNumber",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.attemptNumber}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "attemptNumber",
        serverSortKey: "attemptNumber",
        size: 120,
        exportConfig: {
          exportHeader: "Attempt Number",
          exportValueFn: (stat) => stat.attemptNumber,
        },
      },
      {
        id: "totalQuestions",
        header: "Total Questions",
        accessorKey: "totalQuestions",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.totalQuestions}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "totalQuestions",
        serverSortKey: "totalQuestions",
        size: 130,
        exportConfig: {
          exportHeader: "Total Questions",
          exportValueFn: (stat) => stat.totalQuestions,
        },
      },
      {
        id: "answeredQuestions",
        header: "Answered Questions",
        accessorKey: "answeredQuestions",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.answeredQuestions}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "answeredQuestions",
        serverSortKey: "answeredQuestions",
        size: 150,
        exportConfig: {
          exportHeader: "Answered Questions",
          exportValueFn: (stat) => stat.answeredQuestions,
        },
      },
      {
        id: "correctAnswers",
        header: "Correct Answers",
        accessorKey: "correctAnswers",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.correctAnswers}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "correctAnswers",
        serverSortKey: "correctAnswers",
        size: 130,
        exportConfig: {
          exportHeader: "Correct Answers",
          exportValueFn: (stat) => stat.correctAnswers,
        },
      },
      {
        id: "incorrectAnswers",
        header: "Incorrect Answers",
        accessorKey: "incorrectAnswers",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.incorrectAnswers}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "incorrectAnswers",
        serverSortKey: "incorrectAnswers",
        size: 140,
        exportConfig: {
          exportHeader: "Incorrect Answers",
          exportValueFn: (stat) => stat.incorrectAnswers,
        },
      },
      {
        id: "skippedQuestions",
        header: "Skipped Questions",
        accessorKey: "skippedQuestions",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.skippedQuestions}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "skippedQuestions",
        serverSortKey: "skippedQuestions",
        size: 140,
        exportConfig: {
          exportHeader: "Skipped Questions",
          exportValueFn: (stat) => stat.skippedQuestions,
        },
      },
      {
        id: "rawScore",
        header: "Raw Score",
        accessorKey: "rawScore",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.rawScore}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "rawScore",
        serverSortKey: "rawScore",
        size: 100,
        exportConfig: {
          exportHeader: "Raw Score",
          exportValueFn: (stat) => stat.rawScore,
        },
      },
      {
        id: "maxScore",
        header: "Max Score",
        accessorKey: "maxScore",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.maxScore}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "maxScore",
        serverSortKey: "maxScore",
        size: 100,
        exportConfig: {
          exportHeader: "Max Score",
          exportValueFn: (stat) => stat.maxScore,
        },
      },
      {
        id: "percentage",
        header: "Percentage",
        accessorKey: "percentage",
        accessorFn: (stat) => (
          <span className="text-nowrap">{typeof stat.percentage === 'number' ? stat.percentage.toFixed(2) : '0.00'}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "percentageMin",
        serverSortKey: "percentage",
        size: 120,
        cell: ({ row }) => {
          const stat = row.original;
          // An attempt that was never submitted has no score; "0%" read as a fail.
          if (stat.status !== "COMPLETED") {
            return <span className="text-gray-500">—</span>;
          }
          const percentage = Number(stat.percentage) || 0;
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium">{percentage.toFixed(2)}%</span>
              <Progress value={percentage} className="h-2 w-16" />
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Percentage",
          exportValueFn: (stat) =>
            stat.status === "COMPLETED" ? `${stat.percentage}%` : "",
        },
      },
      {
        id: "Certification",
        header: "Certification",
        accessorKey: "certification",
        accessorFn: (stat) => stat.percentage, // still useful for export/sorting
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "percentageMin",
        serverSortKey: "percentage",
        size: 200,
        cell: ({ row }) => {
          const stat = row.original;
          const test = stat.test;
          if (test?.type === "QUIZ") {
            return <span className="text-gray-500">NA</span>;
          }

          if (test?.type === "ASSESSMENT") {
            const status = certificationStatus(stat);
            if (status === "eligible") {
              return (
                <button
                  className="px-3 text-nowrap py-1 text-sm bg-[#5D93A5] text-white rounded-md"
                  onClick={() =>
                    stat.courseId && handleViewCertificate(stat.courseId)
                  }
                >
                  View certificate
                </button>
              );
            }
            if (status === "in-progress") {
              return <span className="text-gray-500">In progress</span>;
            }
            return <span className="text-red-500">Not Eligible</span>;
          }

          return <span className="text-gray-500">NA</span>; // fallback
        },
        exportConfig: {
          exportHeader: "Certification",
          exportValueFn: (stat) => {
            if (stat.test?.type === "QUIZ") return "NA";
            if (stat.test?.type === "ASSESSMENT") {
              const status = certificationStatus(stat);
              if (status === "eligible") return "Eligible";
              if (status === "in-progress") return "In progress";
              return "Not Eligible";
            }
            return "NA";
          },
        },
      },
      {
        id: "startedAt",
        header: "Started At",
        accessorKey: "startedAt",
        accessorFn: (stat) => stat.startedAt,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "date",
        serverFilterKey: "dateFrom",
        serverSortKey: "startedAt",
        size: 150,
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? new Date(date).toLocaleString() : "-";
        },
        exportConfig: {
          exportHeader: "Started At",
          exportValueFn: (stat) =>
            stat.startedAt ? new Date(stat.startedAt).toLocaleString() : "-",
        },
      },
      {
        id: "completedAt",
        header: "Completed At",
        accessorKey: "completedAt",
        accessorFn: (stat) => stat.completedAt,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "date",
        serverFilterKey: "dateTo",
        serverSortKey: "completedAt",
        size: 150,
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? new Date(date).toLocaleString() : "-";
        },
        exportConfig: {
          exportHeader: "Completed At",
          exportValueFn: (stat) =>
            stat.completedAt
              ? new Date(stat.completedAt).toLocaleString()
              : "-",
        },
      },
    ];
  }, [cdcNumber]);

  return (
    <div className="p-4">
      <div className="font-sans">
        <div className="flex flex-col pr-8">
          <section className="w-full flex flex-col border bg-white p-4 rounded-xl shadow-xl gap-4">
            <div className="flex justify-between items-center">
              <h3 className="uppercase text-lg text-neutral-800 font-bold">
                Test Performance Analytics
              </h3>
            </div>
            <div className="">
              <EnhancedDataTable
                data={[]}
                columns={columns}
                emptyMessage="No test statistics found"
                enableDateRangeFilter={true}
                dateFilterField="date"
                enableGlobalSearch={true}
                globalSearchPlaceholder="Search users, tests, courses..."
                enableColumnVisibility={true}
                enablePagination={true}
                initialPageSize={10}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                enableCSVExport={true}
                enableCustomViews={true}
                viewStorageKey={"test-analytics-view"}
                onViewChange={(view) => {
                  console.log("Test analytics view changed:", view);
                }}
                serverSide={tableServerSide}
                theme="light"
              />
            </div>
          </section>
        </div>
      </div>
      <CertificateModal
        courseId={certificateCourseId}
        open={!!certificateCourseId}
        onOpenChange={(open) => {
          if (!open) setCertificateCourseId(null);
        }}
      />
    </div>
  );
}
